import { Link, useLocation } from "react-router";
import {
  createContext,
  Fragment,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SEGMENT_LABELS } from "@/lib/navigation";

type Labels = Record<string, string>;
const BreadcrumbContext = createContext<{
  labels: Labels;
  setLabel: (segment: string, label: string | null) => void;
} | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<Labels>({});
  // Stable setter: if it changed with `labels`, every consumer effect would re-run and loop.
  const setLabel = useCallback(
    (segment: string, label: string | null) =>
      setLabels((current) => {
        if (label === null) {
          if (!(segment in current)) return current;
          const { [segment]: _removed, ...rest } = current;
          return rest;
        }
        return current[segment] === label ? current : { ...current, [segment]: label };
      }),
    [],
  );
  const value = useMemo(() => ({ labels, setLabel }), [labels, setLabel]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/** Detail pages call this to show a readable name (e.g. an order number) instead of an id. */
export function useBreadcrumbLabel(segment: string, label: string | undefined) {
  const context = useContext(BreadcrumbContext);
  const setLabel = context?.setLabel;
  useEffect(() => {
    if (!setLabel || !label) return;
    setLabel(segment, label);
    return () => setLabel(segment, null);
  }, [setLabel, segment, label]);
}

export function HeaderBreadcrumbs() {
  const { pathname } = useLocation();
  const labels = useContext(BreadcrumbContext)?.labels ?? {};
  const segments = pathname.split("/").filter(Boolean);
  const crumbs = [
    { href: "/", label: "Dashboard" },
    ...segments.map((segment, index) => ({
      href: `/${segments.slice(0, index + 1).join("/")}`,
      label: labels[segment] ?? SEGMENT_LABELS[segment] ?? decodeURIComponent(segment),
    })),
  ];

  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList className="flex-nowrap">
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <Fragment key={crumb.href}>
              {index > 0 ? (
                <BreadcrumbSeparator
                  className={index < crumbs.length - 1 ? "hidden md:block" : undefined}
                />
              ) : null}
              <BreadcrumbItem className={!last ? "hidden md:inline-flex" : "min-w-0"}>
                {last ? (
                  <BreadcrumbPage className="truncate">{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink render={<Link to={crumb.href} />}>{crumb.label}</BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
